import {
  APIError,
  ErrorHandling,
  EVENT_EDITOR_ASSETS,
  EVENT_REQUEST_EDITOR_ASSETS,
  executeWithRetry,
  FatalErrors,
  fetchWrapper,
  findIsland,
  getVersionFromURL,
  iFrameLauncherEvent,
  iFrameLauncherMessage,
  IslandDetails,
  LauncherEventType,
  Logger,
  mocksResponse,
  RenderedClassForStudent,
  RenderedClassForTeacher,
  SessionEntityType,
  StudentManager,
  TeacherManager,
  UserManager,
  UserSession,
} from "@classdojo/monsterverse-utils";
import { DEFAULT_LOCALE, initializeTranslations, isRTLLocale } from "@monsterverse/i18n";
import { StatusCodes } from "http-status-codes";

import { postToApp } from "./MessageHandler";

const DEFAULT_COUNTRY_CODE = "zz"; //Not a valid ISO code, stands for unknown

type TicketResponse = {
  ticket: string;
  version: string;
  groupId: string;
  user: UserSession;
  classes?: RenderedClassForStudent[] | RenderedClassForTeacher[];
  locale: string;
  countryCode?: string;
  region: string;
  desiredRegion: string;
};

function getEnvUrl(base: string) {
  const awsEnv = window._AWS_ENV || "local";
  if (awsEnv === "local") {
    throw new Error("Invalid AWS env");
  } else if (awsEnv === "prod") {
    return base;
  }
  return `${base}-${awsEnv}`;
}

export async function getTicket(groupId: string, entityType: SessionEntityType, body: string | undefined = undefined) {
  return executeWithRetry(async () => {
    const fetchResult = await fetchWrapper(
      "ticket",
      `https://${getEnvUrl(
        "ticket"
      )}.multiplayer.classdojo.com/?groupId=${groupId}&entityType=${entityType}&sessionId=${Logger.getSessionId()}`,
      {
        method: "POST",
        credentials: "include",
        body,
      }
    );

    if (!fetchResult.ok) {
      try {
        const json = await fetchResult.json();
        if (json && json.method && json.url && json.status && json.statusText) {
          Logger.logWarn("API Error", {
            method: json.method,
            url: json.url,
            status: json.status,
            statusText: json.statusText,
          });

          if (fetchResult.status === StatusCodes.UNAUTHORIZED || fetchResult.status === StatusCodes.FORBIDDEN) {
            await setupTranslations();
            ErrorHandling.instance?.triggerFatalError(FatalErrors.UserLoggedOut);
          }
          throw new APIError(`Error in callApi: ${json.status}`, json.method, json, json.statusText);
        }
      } catch (e) {
        if (APIError.instanceof(e)) {
          throw e;
        }
      }
      throw new Error("Failed to get ticket");
    }
    return (await fetchResult.json()) as TicketResponse;
  }, 2);
}

function isEditorBuild() {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const editor = urlSearchParams.get("editor");
  return window._AWS_ENV === "dev" && editor;
}

function devIsland(): IslandDetails {
  const version = isEditorBuild() ? "editor" : getVersionFromURL();
  return {
    id: version,
    ownerName: "Dev's",
    ownerAvatarURL: "",
    islandName: `${version} Island`,
  };
}

async function getEditorAssets() {
  return await new Promise<string>((resolve) => {
    const gotEditorAssets = (assets: string) => {
      window.removeEventListener("message", onGetEditorAssetsIFrameMessage.bind(undefined, gotEditorAssets));
      resolve(assets);
    };
    window.addEventListener("message", onGetEditorAssetsIFrameMessage.bind(undefined, gotEditorAssets));
    console.log("sending request for assets");
    postToApp({ eventName: EVENT_REQUEST_EDITOR_ASSETS });
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function onGetEditorAssetsIFrameMessage(resolve: (assets: string) => void, ev: MessageEvent<any>) {
  if (ev.data.eventName !== EVENT_EDITOR_ASSETS) {
    return;
  }

  resolve(ev.data.eventValues[0]);
}

let lastTicket: TicketResponse | undefined;
// eslint-disable-next-line sonarjs/cognitive-complexity
export async function getAWSHostURLs(groupID: string, entityType: SessionEntityType) {
  let wsUrl: URL;
  let hubUrl: string;
  let island: IslandDetails;
  let countryCode: string;
  let serverRegion: string;
  let desiredServerRegion: string;

  const env = window._ENV || "local";
  if (env === "local" && !window._NGROK_HOST) {
    const localIP = window._LOCALIP || "localhost";
    wsUrl = new URL(`wss://${localIP}:8080`);
    wsUrl.searchParams.set("ticket", "fakeTicket");
    wsUrl.searchParams.set("sessionId", Logger.getSessionId());
    await fetchWrapper("dev", `https://${localIP}:8080/claimed?groupId=local`, { method: "POST", mode: "no-cors" });

    hubUrl = `https://${localIP}:8788`;

    island = {
      id: "Local",
      ownerName: "Local's",
      ownerAvatarURL: "",
      islandName: `Local Island`,
    };
    countryCode = "us";
    serverRegion = "us-east-1";
    desiredServerRegion = "us-east-1";
  } else {
    const awsEnv = window._AWS_ENV || "local";
    let ticketBody: string | undefined;
    if (isEditorBuild()) {
      ticketBody = await getEditorAssets();
    }
    lastTicket = await getTicket(awsEnv === "dev" ? devIsland().id : groupID, entityType, ticketBody);
    wsUrl = new URL(`wss://${getEnvUrl("ws")}.multiplayer.classdojo.com`);
    wsUrl.searchParams.set("ticket", lastTicket.ticket);
    wsUrl.searchParams.set("sessionId", Logger.getSessionId());

    let hubVersion = lastTicket.version;
    if (isEditorBuild()) {
      hubVersion = hubVersion.replace("editor", "dev");
    }
    hubUrl = `https://clients.multiplayer.classdojo.com/hub/${hubVersion}/index.html`;

    //Locate island from returned user data
    UserManager.instance.user = lastTicket.user;
    if (lastTicket.classes) {
      if (entityType === "teacher") {
        TeacherManager.instance.classes = lastTicket.classes as RenderedClassForTeacher[];
      } else {
        StudentManager.instance.classes = lastTicket.classes as RenderedClassForStudent[];
      }
    }
    Logger.setEntityId(UserManager.instance.getEntityId());

    if (awsEnv === "dev") {
      island = devIsland();
    } else {
      island = findIsland(lastTicket.groupId, entityType)!;
    }

    countryCode = lastTicket.countryCode ?? DEFAULT_COUNTRY_CODE;
    serverRegion = lastTicket.region;
    desiredServerRegion = lastTicket.desiredRegion;
  }

  await setupTranslations();

  //Datadog expects United Kingdom to be under "gb" instead of "uk"
  if (countryCode === "uk") countryCode = "gb";

  return { wsUrl, hubUrl, island, countryCode, serverRegion, desiredServerRegion };
}

export async function setupTranslations() {
  await initializeTranslations(lastTicket?.locale || DEFAULT_LOCALE, {
    isLocal: mocksResponse(),
    build: window.build,
    logger: (level, message) => {
      switch (level) {
        case "error":
          console.error(message);
          Logger.logError(message);
          break;
        case "warn":
          console.warn(message);
          Logger.logWarn(message);
          break;
        case "info":
          console.log(message);
          Logger.logInfo(message);
          break;
      }
    },
  });

  if (isRTLLocale()) {
    // add special marker class to document body element so HTML elements
    // with class `supportsRtl` can opt-in to get rendered with `direction: rtl`
    window.document.body.classList.add("rtl");
  }
}

let iframeEl: HTMLIFrameElement | undefined;
export function handleUserDataRequests(iframe: HTMLIFrameElement) {
  iframeEl = iframe;
  window.addEventListener("message", onIFrameMessage);
}

function onIFrameMessage(ev: MessageEvent<iFrameLauncherMessage>) {
  if (ev?.data?.iFrameLauncherEvent !== iFrameLauncherEvent || lastTicket === undefined) {
    return;
  }

  switch (ev.data.type) {
    case LauncherEventType.GetUser:
      postMessage(LauncherEventType.GetUser, { user: lastTicket.user });
      break;
    case LauncherEventType.GetClasses:
      postMessage(LauncherEventType.GetClasses, { classes: lastTicket.classes });
      break;
    case LauncherEventType.GetLocale:
      postMessage(LauncherEventType.GetLocale, { locale: lastTicket.locale || DEFAULT_LOCALE });
      break;
  }
}

function postMessage(type: LauncherEventType, data: Record<string, unknown>) {
  iframeEl?.contentWindow?.postMessage({ iFrameLauncherEvent, type, data }, "*");
}
